import React, { FC, useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
// actions
import dyopsAction from '../../../redux/actions/dyops';
// components
import EyePreparation from '../../atoms/eye';
import SelfTestInfantControls from '../../moleculs/selfTestInfantControls';
import SelfTestOvalControls from '../../moleculs/selfTestOvalControls';
// types
import { appTheme, loadingType } from '../../../types/types';
import { dyopsType } from '../../../types/dyops';
// style
import './index.scss';
// helpers
import { handleDyopsControl } from '../../../tools/dyopsControl';
import { applyInfantModeToDyops, isInfantDyopsSession } from '../../../tools/infantDefaults';
import { isOvalExamMode } from '../../../tools/resolveExamMode';
// localization
import strings from '../../../localization';
import EyePreparationDark from '../../atoms/eye/eyeDark';
import { useTheme } from '../../../hooks/theme';

interface SelfTestProps {
  appLoading: loadingType;
  isConnected: boolean;
  socket: React.MutableRefObject<any>;
  isIntegration: string;
  theme: appTheme;
  dyops: dyopsType;
  sessionTestType?: string;
  screenRatio?: number;
}

const SelfTest: FC<SelfTestProps> = ({
  appLoading,
  isConnected,
  socket,
  isIntegration,
  dyops,
  sessionTestType,
  theme,
  screenRatio,
}: SelfTestProps) => {
  const [randomNumbers, setRandomNumbers] = useState<Array<number>>([]);

  const history = useHistory();
  const dispatch = useDispatch();
  const { isDark } = useTheme();
  const isInfantSession = isInfantDyopsSession(dyops, sessionTestType);
  const isOvalSession = isOvalExamMode(dyops);
  const effectiveDyops = useMemo(
    () => (isInfantSession ? applyInfantModeToDyops(dyops, 'infant') : dyops),
    [dyops, isInfantSession],
  );

  const PreparationComponent = useMemo(() => (theme === 'dark' ? EyePreparationDark : EyePreparation), [theme]);

  const activeDyopRandom = (): 'left' | 'right' => {
    const random = Math.floor(Math.random() * (3 - 1) + 1);

    if (
      randomNumbers.length >= 3 &&
      randomNumbers.slice(randomNumbers.length - 3, randomNumbers.length).every((item) => item === random)
    ) {
      setRandomNumbers([...randomNumbers, random === 2 ? random - 1 : random + 1]);

      return (random === 2 ? random - 1 : random + 1) !== 2 ? 'right' : 'left';
    } else {
      setRandomNumbers([...randomNumbers, random]);

      return random % 2 !== 0 ? 'right' : 'left';
    }
  };

  const handleControl = (action: string) => {
    handleDyopsControl(
      dispatch,
      socket.current,
      effectiveDyops,
      action,
      history,
      isIntegration === 'integration' ? true : false,
      activeDyopRandom,
      '',
      isDark,
    );
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const action = e.currentTarget.id || e.currentTarget.value;
    if (action) {
      handleControl(action);
    }
  };

  // Pushes live oval preview updates from mobile to the PC monitor while dragging.
  const handleOvalPreview = useCallback(
    (next: dyopsType) => {
      dispatch(dyopsAction.getDyops(next));
      socket.current?.emit('emitUpdateDoctor', { dyops: next });
    },
    [dispatch, socket],
  );

  // Pushes oval slider commits from mobile to the room socket and Redux.
  const handleOvalCommit = useCallback(
    (next: dyopsType) => {
      dispatch(dyopsAction.getDyops(next));
      socket.current?.emit('emitUpdateDoctor', { dyops: next });
    },
    [dispatch, socket],
  );

  // Renders default mobile answer buttons for the VA exam mode.
  const renderVaControls = () => (
    <div className="buttons">
      <div className="buttons__item_help-wrapper">
        <button value="increase" className="buttons__item buttons__item_help" onClick={handleClick}>
          <span>{strings.selfTest.control.dontKnow}</span>
        </button>
      </div>
      <div className="buttons__inner">
        <button
          value={dyops.active === 'left' ? 'decrease' : 'increase'}
          className="buttons__item buttons__item_left"
          onClick={handleClick}
        >
          <span>{strings.testPage.left}</span>
        </button>
        <button
          value={dyops.active === 'right' ? 'decrease' : 'increase'}
          className="buttons__item buttons__item_right"
          onClick={handleClick}
        >
          <span>{strings.testPage.right}</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {!appLoading.status && isConnected && (
        <div className={`self-test${dyops.isStarted ? ' self-test--answers' : ''}`}>
          {!dyops.isStarted && !isInfantSession && (dyops.closeLeftEye || dyops.closeRightEye) ? (
            <PreparationComponent
              eye={dyops.closeLeftEye ? 'left' : 'right'}
              type="selfTest"
              prepSurface="device"
              distance={dyops.result.distance}
              unit={dyops.result.unit}
              footSteps={dyops.result.footSteps}
            />
          ) : (
            dyops.isStarted &&
            (isInfantSession ? (
              <SelfTestInfantControls dyops={effectiveDyops} onAction={handleControl} />
            ) : isOvalSession ? (
              <SelfTestOvalControls
                dyops={effectiveDyops}
                screenRatio={screenRatio}
                onPreviewDyopsChange={handleOvalPreview}
                onCommit={handleOvalCommit}
                onFinishEye={() => handleControl('finishOvalEye')}
              />
            ) : (
              renderVaControls()
            ))
          )}
        </div>
      )}
    </>
  );
};

export default SelfTest;
